import React, { FC, ReactNode, useCallback } from 'react'

import { css, cx } from '@linaria/core'
import { styled } from '@linaria/react'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'

import { BaseImage } from 'components/designSystem/BaseImage/BaseImage'
import { IncognitoCover } from 'components/designSystem/IncognitoCover/IncognitoCover'
import { breakpoints } from 'components/designSystem/shared/breakpoints'
import { calculatePosition } from 'components/page/ForeignProfile/Photos/function/calculatePosition'
import { PhotosType } from 'components/page/ForeignProfile/Photos/PhotosTile/PhotosTile.types'
import { push } from 'functions/router'
import { GQLPhotoFaceCenter } from 'generated-graphql/graphql'
import { useShallowEqualSelector } from 'hooks/useShallowEqualSelector'

import { usePhotoUrl } from '../../ProfilePhoto/hooks/usePhotoUrl'

export const Photo: FC<{
  photoId: number
  xHuge?: string | null
  huge: string
  width: number
  height: number
  incognito?: boolean
  coordinates?: Partial<GQLPhotoFaceCenter>
  mask?: ReactNode
}> = ({
  photoId,
  xHuge,
  huge,
  width,
  height,
  incognito,
  coordinates,
  mask,
}) => {
  const dispatch = useDispatch()
  const photoUrl = usePhotoUrl(photoId)
  const { javaScriptEnabled } = useShallowEqualSelector(
    ({ systemReducer: { javaScriptEnabled } }) => ({
      javaScriptEnabled,
    })
  )

  const handleClick = useCallback(() => {
    dispatch(push(photoUrl))
  }, [dispatch, photoUrl])

  const photoPosition = calculatePosition({
    width,
    height,
    $type: xHuge ? PhotosType.xHuge : PhotosType.huge,
    $coordinates: coordinates,
  })

  const children = (
    <>
      <Picture data-name={`photoviewer-${photoId}-photo`}>
        {xHuge && (
          <source
            srcSet={xHuge}
            width={width}
            height={height}
            type="image/webp"
            data-name={`photoviewer-${photoId}-photo-xhuge`}
            className={cx(imageCss)}
            draggable={false}
            style={{
              objectPosition: `${photoPosition}`,
            }}
          />
        )}
        <BaseImage
          imageSource={huge}
          width={width}
          height={height}
          loading="lazy"
          data-name={`photoviewer-${photoId}-photo-huge`}
          className={cx(imageCss)}
          draggable={false}
          style={{
            objectPosition: `${photoPosition}`,
          }}
        />
      </Picture>
      {mask}
      {incognito && <IncognitoCover />}
    </>
  )

  /**
   * Если ссылка внутри useDrag, а не на корневом элементе, то придется делать вот
   * https://youtrack.mamba.ru/issue/M-10906
   */
  if (javaScriptEnabled) {
    return (
      <div
        className={cx(parentCss)}
        onClick={handleClick}
        data-name={`photoviewer-${photoId}-action`}
      >
        {children}
      </div>
    )
  } else {
    return (
      <Link
        to={photoUrl}
        className={cx(parentCss)}
        data-name={`photoviewer-${photoId}-action`}
      >
        {children}
      </Link>
    )
  }
}

const parentCss = css`
  display: flex;
  width: 100%;
  height: 100%;
  position: relative;

  @media screen and (max-width: ${breakpoints.tablet - 1}px) {
    display: none;

    &:first-child {
      display: flex;
    }
  }
`

const Picture = styled.picture`
  display: flex;
  width: 100%;
  height: 100%;
  flex-grow: 1;
  pointer-events: none;
  user-select: none;
`

const imageCss = css`
  object-fit: cover;
  width: 100%;
  height: 100%;
  pointer-events: none;
  user-select: none;
  position: absolute;
  left: 0;
  top: 0;
`
